import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import {
  GetShippingDocumentQuery,
  ShippingDocumentKind,
  UnitType,
  WasteKind,
  WasteShippingDocumentInput,
} from "generated-graphql/graphql";

/** Quantity held as a string so a cleared input reads as "", not NaN — `toFloat` turns it back into a nullable Float on submit. */
export interface ShippingDocumentLineFormRow {
  description: string;
  quantity: string;
  unit: UnitType | null;
  declaredWasteKind: WasteKind | null;
  federalWasteCodes: string;
  stateWasteCodes: string;
  /**
   * Carried, not edited. A save replaces the line set wholesale and writes
   * `wasteStreamId ?? null`, so a row that reaches the mutation without these
   * clears the line's match to its stream and container. The form has no
   * control for either — matching happens elsewhere — so they ride along
   * untouched.
   *
   * Read from the line's own id fields, never from the resolved `wasteStream` /
   * `container` objects. Those are authorization-filtered: `container` comes
   * back null when it has moved to a facility this reader cannot open, and
   * rebuilding the row from it would turn an unrelated header edit into a
   * silent delete of the association.
   */
  wasteStreamId: string | null;
  containerId: string | null;
}

export interface ShippingDocumentFormValues {
  kind: ShippingDocumentKind;
  documentNumber: string;
  generatorName: string;
  transporterName: string;
  destinationName: string;
  /**
   * ISO date ("YYYY-MM-DD"), "" or null — every shape `DateField` writes. Its
   * `clearable` defaults to `!required` and this dialog passes no `required`,
   * so the picker has a Clear action, and a partial date whose `toISODate()`
   * returns null lands here too.
   */
  shipDate: string | null;
  lines: ShippingDocumentLineFormRow[];
}

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;

function toFloat(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = safeParseFloat(trimmed);
  return parsed != null && Number.isFinite(parsed) ? parsed : null;
}

function toCodesArray(value: string): string[] {
  return value
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");
}

function toText(value: string | null): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export const EMPTY_LINE: ShippingDocumentLineFormRow = {
  description: "",
  quantity: "",
  unit: null,
  declaredWasteKind: null,
  federalWasteCodes: "",
  stateWasteCodes: "",
  wasteStreamId: null,
  containerId: null,
};

/** The document as it reads into the edit form, in document order. */
export function toFormValues(document: Document): ShippingDocumentFormValues {
  return {
    kind: document.kind,
    documentNumber: document.documentNumber ?? "",
    generatorName: document.generatorName ?? "",
    transporterName: document.transporterName ?? "",
    destinationName: document.destinationName ?? "",
    shipDate: document.shipDate ?? "",
    lines: document.lines.map((line) => ({
      description: line.description ?? "",
      quantity: line.quantity != null ? String(line.quantity) : "",
      unit: line.unit ?? null,
      declaredWasteKind: line.declaredWasteKind ?? null,
      federalWasteCodes: line.federalWasteCodes.join(", "),
      stateWasteCodes: line.stateWasteCodes.join(", "),
      wasteStreamId: line.wasteStreamId ?? null,
      containerId: line.containerId ?? null,
    })),
  };
}

/**
 * The form's values as a save input. `id`/`facilityId` come from the document
 * being edited (facilityId is always re-sent because the input requires it,
 * even though a save on an existing document ignores it — see
 * `WasteShippingDocumentInput.facilityId`'s doc comment). `lines` is always
 * the full set: a save wholesale-replaces it, so a partial list here would
 * silently drop rows the reviewer never touched.
 */
export function toSaveInput(
  values: ShippingDocumentFormValues,
  { id, facilityId }: { id: string; facilityId: string }
): WasteShippingDocumentInput {
  return {
    id,
    facilityId,
    kind: values.kind,
    documentNumber: toText(values.documentNumber),
    generatorName: toText(values.generatorName),
    transporterName: toText(values.transporterName),
    destinationName: toText(values.destinationName),
    shipDate: toText(values.shipDate),
    lines: values.lines.map((line) => ({
      description: toText(line.description),
      quantity: toFloat(line.quantity),
      unit: line.unit,
      declaredWasteKind: line.declaredWasteKind,
      federalWasteCodes: toCodesArray(line.federalWasteCodes),
      stateWasteCodes: toCodesArray(line.stateWasteCodes),
      wasteStreamId: line.wasteStreamId,
      containerId: line.containerId,
    })),
  };
}
